import { useMemo } from 'react';
import { pick } from 'ramda';
import { useTranslations } from 'js/hooks/i18n';
import { TFormFieldDepositLimit } from 'js/components/common/Form/types';
import { EDITABLE_DD_MODES } from 'js/components/common/Fields/EditableDropDown/constants';
import { DEPOSIT_LIMITS_PREDEFINED_TYPE } from 'js/hooks/socket/hooks/useGetCasinoDepositLimitsSettings/constants';

import * as Types from '../types';
import { IMS_UNLIMITED_AMOUNT } from '../constants';
import { useDynamicReducerSelector } from '../../../store';

export const useDepositLimitsConfig = (field?: TFormFieldDepositLimit) => {
    const { limitsSettings: { deposit: limitsSettings }, limitsCurrency } = useDynamicReducerSelector(pick([
        'limitsSettings', 'limitsCurrency'
    ]));
    const translate = useTranslations('playtech.mobile.system.form.limits.');

    return useMemo((): Types.TDepositLimitsConfig | undefined => {
        if (!limitsSettings || !field) {
            return;
        }

        const {
            availableTimePeriods, predefinedLimits, predefinedType, noLimitsAllowed
        } = limitsSettings;
        const isDisabledType = predefinedType === DEPOSIT_LIMITS_PREDEFINED_TYPE.DISABLED;
        const { isDailyEnabled, isWeeklyEnabled, isMonthlyEnabled } = field;
        const limit = (isDisabledType ? [] : predefinedLimits).find(({ currency }) => currency === limitsCurrency);

        let mode = EDITABLE_DD_MODES.ALL;

        if (predefinedType === DEPOSIT_LIMITS_PREDEFINED_TYPE.FORCED) {
            mode = EDITABLE_DD_MODES.LIST_ONLY;
        } else if (!noLimitsAllowed && isDisabledType) {
            mode = EDITABLE_DD_MODES.TEXT_ONLY;
        }

        return {
            ...limitsSettings,
            mode,
            displayValuesMap: {
                0: '',
                ...noLimitsAllowed ? { [IMS_UNLIMITED_AMOUNT]: translate('unlimited') } : {}
            },
            limit: {
                ...limit,
                currency: limitsCurrency,
                dailyDefaultLimit: Number(limit?.dailyDefaultLimit) ? limit?.dailyDefaultLimit : '0',
                weeklyDefaultLimit: Number(limit?.weeklyDefaultLimit) ? limit?.weeklyDefaultLimit : '0',
                monthlyDefaultLimit: Number(limit?.monthlyDefaultLimit) ? limit?.monthlyDefaultLimit : '0'
            },
            isDailyEnabled: availableTimePeriods.includes('daily') && isDailyEnabled,
            isWeeklyEnabled: availableTimePeriods.includes('weekly') && isWeeklyEnabled,
            isMonthlyEnabled: availableTimePeriods.includes('monthly') && isMonthlyEnabled,
            dataList: [
                ...(limit?.allowedLimits || []).map(allowedLimit => ({ value: allowedLimit, displayValue: allowedLimit })),
                ...noLimitsAllowed ? [{ value: IMS_UNLIMITED_AMOUNT, displayValue: translate('unlimited') }] : []
            ]
        };
    }, [translate, field, limitsSettings, limitsCurrency]);
};
